import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/base/Pagination";
import { PAGE_SIZE } from "@/constants/filters";
import { films } from "@/mocks/films";
import type { FilmKind } from "@/types/film";
import CatalogFilters, { type CatalogFilterValues } from "@/pages/catalog/components/CatalogFilters";

interface CatalogListingProps {
  kind: FilmKind | "all";
  searchQuery?: string;
}

const defaults: CatalogFilterValues = {
  region: "全部地区",
  decade: "全部年代",
  rating: "全部评分",
  episodes: "全部集数",
  sort: "latest",
};

const ratingThreshold: Record<string, number> = {
  "9.0 分以上": 9,
  "8.5 分以上": 8.5,
  "8.0 分以上": 8,
};

function matchesKeyword(filmTitle: string, fields: string[], query: string) {
  const target = [filmTitle, ...fields].join(" ").toLowerCase();
  return target.includes(query.toLowerCase());
}

export default function CatalogListing({ kind, searchQuery = "" }: CatalogListingProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const values: CatalogFilterValues = {
    region: searchParams.get("region") || defaults.region,
    decade: searchParams.get("decade") || defaults.decade,
    rating: searchParams.get("rating") || defaults.rating,
    episodes: searchParams.get("episodes") || defaults.episodes,
    sort: searchParams.get("sort") || defaults.sort,
  };

  const isFiltered =
    values.region !== defaults.region ||
    values.decade !== defaults.decade ||
    values.rating !== defaults.rating ||
    values.episodes !== defaults.episodes;

  const baseFilms = useMemo(
    () => (kind === "all" ? films : films.filter((film) => film.kind === kind)),
    [kind],
  );

  const filtered = useMemo(() => {
    const keyword = searchQuery.trim();
    const threshold = ratingThreshold[values.rating] ?? 0;

    const result = baseFilms.filter((film) => {
      if (keyword) {
        const haystack = [
          film.originalTitle,
          film.director,
          ...film.cast,
          ...film.genres,
          film.region,
        ];
        if (!matchesKeyword(film.title, haystack, keyword)) return false;
      }
      if (values.region !== defaults.region && film.region !== values.region) return false;
      if (values.decade === "2020年代" && film.year < 2020) return false;
      if (values.decade === "2010年代" && (film.year < 2010 || film.year >= 2020)) return false;
      if (threshold && film.rating < threshold) return false;
      if (values.episodes === "12 集以内" && (film.episodes ?? 99) > 12) return false;
      if (values.episodes === "13 - 24 集" && ((film.episodes ?? 0) < 13 || (film.episodes ?? 0) > 24)) return false;
      return true;
    });

    return result.sort((a, b) => {
      if (values.sort === "rating") return b.rating - a.rating;
      if (values.sort === "views") return b.views - a.views;
      return b.year - a.year || b.rating - a.rating;
    });
  }, [baseFilms, searchQuery, values.region, values.decade, values.rating, values.episodes, values.sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleChange = (key: keyof CatalogFilterValues, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === defaults[key]) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const handleReset = () => {
    setSearchParams({}, { replace: true });
    setPage(1);
  };

  return (
    <section className="w-full px-4 py-8 md:px-10 md:py-10">
      <CatalogFilters
        values={values}
        showEpisodes={kind === "series"}
        onChange={handleChange}
        onReset={handleReset}
        isFiltered={isFiltered}
      />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-foreground-600">
          共 <strong className="text-sm font-medium text-primary-600">{filtered.length}</strong> 部作品
          {searchQuery && (
            <span className="ml-1">
              · 关键词「<span className="text-foreground-900">{searchQuery}</span>」
            </span>
          )}
          <span className="ml-2 text-foreground-400">（演示数据，共 {baseFilms.length} 条记录）</span>
        </p>
        <p className="text-xs text-foreground-400">
          第 {currentPage} / {totalPages} 页
        </p>
      </div>

      {visible.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {visible.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-2xl" />
          </span>
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有符合条件的作品</h3>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            试着放宽评分或年代条件，或者换一个搜索关键词。当前演示库收录的作品数量有限。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选条件
          </button>
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
    </section>
  );
}